/* eslint-disable */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */

import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SearchResultModalComponent } from './components/search-modal/search-modal.component';
import { ActiveData, SearchCounts, SearchDataModel, SearchHistoryModal, SearchHistoryResultsModal, SearchRequesModel, SearchResultModal, tabValues } from './contracts/modal';
import { regionsContextPointSubTypeId, SalesRegionsContextPointSubTypeId } from './enums/search.enums';
import { GlobalSearchFiltersService } from './services/global-search-filters.service';

@Component({
  selector: 'nalco-global-search-filters',
  templateUrl: './global-search-filters.component.html',
  styleUrls: ['./global-search-filters.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class GlobalSearchFiltersComponent implements OnInit, OnDestroy {
  @Input() public searchData!: SearchDataModel;
  @Input() public searchCounts!: SearchCounts;
  @Input() public customModalClass?: string;
  @Input() public selectedData?: SearchResultModal;
  @Input() public showSelected = true;
  @Input() public subscriptions: Subscription[];
  @Output() public selectedSearchedResult = new EventEmitter<SearchResultModal>();
  @Output() public multipleSelectedSearchedResult? = new EventEmitter<SearchResultModal[]>();
  @Output() public isClearSelectionActivated = new EventEmitter<boolean>();
  public isRotated = false;
  public mostRecentData: SearchResultModal | null = null;
  public isLoading = false;
  private completedTabs = 0;
  private totalTabsToLoad = 0;
  private instanceId = 'Global Search Component';
  private readonly timeOut = 700000;
  private tabSelected: tabValues;
  private activeDataCheckInterval: ReturnType<typeof setInterval> | null = null;
  private hasEmittedNull = false;

  public constructor(private readonly modalService: NgbModal,
    private globalSearchFiltersService: GlobalSearchFiltersService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) { }

  /**
   * Determines if search counts should be displayed.
   */
  public get shouldShowSearchCounts(): boolean {
    return !this.selectedData?.secondaryText &&
      (this.searchCounts !== null) &&
      this.selectedData?.filterType !== 'COMMON.GLOBAL_SEARCH.SITES';
  }
  /**
   * Determines if account count should be displayed.
   */
  public get shouldShowAccountCount(): boolean {
    return (this.searchCounts?.corporateAccountCount !== null) && (this.searchCounts?.corporateAccountCount ?? 0) > 1;
  }

  public get shouldShowSiteCount(): boolean {
    return (this.searchCounts?.siteCount !== null) && (this.searchCounts?.siteCount ?? 0) > 1;
  }

  public ngOnInit(): void {
    this.isLoading = true;

    if (this.searchData?.SearchTabs?.length) {
      this.searchData.SearchTabs.forEach((tab, index) => {
        tab.isActive = index === 0;
      });
    }

    this.fetchSearchHistoryForAllTabs(true);
    this.cdr.markForCheck();
  }

  /**
   * Opens a modal for search results and emits the selected result.
   */
  public openModal(): void {
    this.toggleArrowRotation();
    const modalRef = this.modalService.open(SearchResultModalComponent, {
      backdrop: true,
      keyboard: false,
      windowClass: this.customModalClass ?? 'one-search-modal-popup',
      size: 'lg',
    });

    modalRef.componentInstance.searchData = this.searchData;
    modalRef.componentInstance.selectedSearchedResult.subscribe((value: SearchResultModal | SearchResultModal[]) => {
      this.handleSelectedResult(value);
    });
    modalRef.componentInstance.isClearSelectionEmitted.subscribe((value: boolean) => {
      this.isClearSelectionActivated.emit(value);
    });
    modalRef.componentInstance.multipleSelectedSearchedResult?.subscribe((value: SearchResultModal[]) => {
      this.handleSelectedResult(value);
      this.multipleSelectedSearchedResult?.emit(value);
    });
    void modalRef.result
      .then((result: SearchResultModal | SearchResultModal[]) => {
        this.toggleArrowRotation();
        if (result) {
          this.fetchSearchHistoryForAllTabs(false);
        }
      })
      .catch(() => {
        this.toggleArrowRotation();
      });
  }
  /**
   * Generates a secondary text string based on the selection hierarchy of a given search history item.
   * Each context point name in the hierarchy is concatenated with a separator icon, except for the last element.
   *
   * @param item - The search history item containing the selection hierarchy.
   * @returns A concatenated string of context point names with separators, or an empty string if the hierarchy is empty.
   */
  public createSecondaryTextOnNames(item: SearchHistoryModal | SearchResultModal): string {
    if (!item?.selectionHierarchy?.length) {
      return '';
    } const cleanedItem = this.removeDuplicateName(item);
    const contextPointNames = cleanedItem.selectionHierarchy.map((element, index) => {
      return this.formatContextPointName(element, index, cleanedItem.selectionHierarchy.length);
    });
    return contextPointNames.join(' ');
  }

  /**
     * @description Fetches the search history for all tabs in searchData.SearchTabs.
     * This method loops through each tab and calls fetchSearchHistory for each one,
     * mapping the response back to the respective tab's recentsData property.
     * 
     * @returns {void}
     */

  public fetchSearchHistoryForAllTabs(loadHistory: boolean): void {
    if (!this.searchData?.SearchTabs?.length) {
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.completedTabs = 0;
    this.totalTabsToLoad = this.searchData.SearchTabs.filter(tab => tab.contextPointTypeId).length;

    if (this.totalTabsToLoad === 0) {
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.searchData.SearchTabs.forEach((tab, index) => {
      if (tab.contextPointTypeId) {
        this.fetchSearchHistory(tab, index, loadHistory);
      }
    });
  }

  /**
 * @description Fetches the search history for a specific tab based on the provided contextPointTypeId and contextPointSubTypeId.
 * This method retrieves the user's search history from the `globalSearchFiltersService`
 * and processes the data to map it with search and filter naming conventions.
 * 
 * - The search history is transformed to include additional properties such as
 *   `primaryText`, `secondaryText`, and filter-specific keys.
 * - The processed search history is then mapped to the specific tab's recentsData.
 * 
 * Handles loading state and error scenarios:
 * - On success, updates the tab's `recentsData` and processes it further.
 * - On error, clears recent data for the specific tab.
 * 
 * @param {tabValues} tab - The tab object containing contextPointTypeId and other properties
 * @param {number} tabIndex - The index of the tab in the SearchTabs array
 * @returns {void}
 */
  public fetchSearchHistory(tab: tabValues, tabIndex: number, updateRecentSelection: boolean): void {
    if (!this.subscriptions) {
      this.subscriptions = [];
    }

    const contextTypeId = (this.translate.instant(tab.name) === this.translate.instant('COMMON.GLOBAL_SEARCH.CORPORATE_ACCOUNT')) ? 1 : null;

    this.subscriptions.push(
      this.globalSearchFiltersService.getUserContextSearchHistory(
        tab.contextPointTypeId,
        contextTypeId
      ).subscribe({
        next: (data) => {
          if (data && data.length > 0) {
            const processedData = data.map(item => {
              return {
                ...item,
                primaryText: item.contextPointName,
                secondaryText: this.createSecondaryTextOnNames(item),
                displaySecondaryText: true,
                filterType: this.translate.instant(tab.name),
                [tab.contextPointIdKeys[0]]: item.contextPointId,
                [tab.contextPointIdKeys[1]]: item.contextPointName,
              };
            });

            this.searchData.SearchTabs[tabIndex].recentsData = this.mapWithFilterNaming(processedData, tab);
          } else {
            this.searchData.SearchTabs[tabIndex].recentsData = [];
          }

          this.completedTabs++;
          if (this.completedTabs === this.totalTabsToLoad) {
            const hasRecentData = this.searchData.SearchTabs.some(tab =>
              Array.isArray(tab.recentsData) && tab.recentsData.length > 0
            );
            if (this.searchData.enableMostRecentSelection && updateRecentSelection) {
              if (hasRecentData) {
                const initialSelectionTab = this.searchData.SearchTabs.find(tab => tab.initialActive === true);
                if (initialSelectionTab && (!initialSelectionTab.recentsData || initialSelectionTab.recentsData.length === 0)) {
                  initialSelectionTab.initialActive = false;
                }

                this.updateMostRecentData();
              } else {
                this.handleNoRecentDataScenario();
              }
            } else {
              this.isLoading = false;
            }
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.searchData.SearchTabs[tabIndex].recentsData = [];
          this.completedTabs++;
          if (this.completedTabs === this.totalTabsToLoad) {
            this.isLoading = false;
            this.cdr.markForCheck();
            this.selectedSearchedResult.emit(null);
          }
        }
      })
    );
  }

  public ngOnDestroy(): void {
    if (this.activeDataCheckInterval) {
      clearInterval(this.activeDataCheckInterval);
    }
    if (this.subscriptions) {
      this.subscriptions.forEach(sub => sub.unsubscribe());
    }
  }


  /**
   * Updates the mostRecentData variable by finding the most recent item across all tabs
   * based on the createdOn property. Only considers tabs with recentsData length > 0.
   * If most recent data is found, activates that tab and deactivates all others.
   * If no recent data is found, keeps the first tab active by default.
   */
  private updateMostRecentData(): void {
    if (!this.searchData?.SearchTabs?.length) {
      return;
    }
    const initialSelectionTab = this.searchData.SearchTabs.find(tab => tab.initialActive === true);
    let mostRecentItem: SearchHistoryResultsModal | null = null;
    let mostRecentTabIndex = -1;

    const tabsToSearch = initialSelectionTab ? [initialSelectionTab] : this.searchData.SearchTabs;
    const startIndex = initialSelectionTab ? this.searchData.SearchTabs.indexOf(initialSelectionTab) : 0;

    tabsToSearch.forEach((tab, relativeIndex) => {
      const tabIndex = initialSelectionTab ? startIndex : relativeIndex;

      if (Array.isArray(tab.recentsData) && tab.recentsData.length > 0) {
        const validItems = tab.recentsData.filter(item =>
          item?.createdOn && !isNaN(Date.parse(item.createdOn))
        );

        if (validItems.length > 0) {
          const tabMostRecent = validItems.reduce((latest, current) => {
            const latestDate = new Date(latest.createdOn);
            const currentDate = new Date(current.createdOn);
            return currentDate > latestDate ? current : latest;
          });

          if (!mostRecentItem || new Date(tabMostRecent.createdOn) > new Date(mostRecentItem.createdOn)) {
            mostRecentItem = tabMostRecent;
            mostRecentTabIndex = tabIndex;
          }
        }
      }
    });

    // Set initialSelection to false after processing
    if (initialSelectionTab) {
      initialSelectionTab.isActive = false;
    }

    this.mostRecentData = mostRecentItem;
    if (mostRecentItem && mostRecentTabIndex >= 0) {
      this.selectedData = {
        ...mostRecentItem,
        primaryText: mostRecentItem.primaryText ?? mostRecentItem.contextPointName,
        secondaryText: mostRecentItem.secondaryText ?? this.createSecondaryTextOnNames(mostRecentItem)
      } as SearchResultModal;
      this.activateTabWithMostRecentData(mostRecentItem as SearchResultModal, mostRecentTabIndex);
      let matchingHierarchy = null;
      let searchInFields = this.tabSelected.contextPointIdKeys;
      if (mostRecentItem.filterName && this.tabSelected.hierarchyValues?.length > 0) {
        matchingHierarchy = this.tabSelected.hierarchyValues.find(hierarchy =>
          hierarchy.value === mostRecentItem.filterName ||
          this.globalSearchFiltersService.getFilterNameMapping(mostRecentItem.filterName) === hierarchy.value
        );
        if (matchingHierarchy?.contextPointIdKeys) {
          searchInFields = matchingHierarchy.contextPointIdKeys;
        }
      }

      const searchPayloadBody = {
        searchText: '',
        count: 1,
        searchInFields: searchInFields,
        responseFields: this.tabSelected.responseFileds,
        filterCondition: 'or',
        filterBy: matchingHierarchy?.contextPointIdKeys
          ? { [matchingHierarchy.contextPointIdKeys[0]]: this.selectedData.contextPointId.toString() }
          : { [this.tabSelected.contextPointIdKeys[0]]: this.selectedData.contextPointId.toString() }

      }
      this.getContextSearchResult(searchPayloadBody, matchingHierarchy?.contextPointIdKeys[0]);
    }
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  /**
   * Handles the selected search result by setting secondary text and emitting the result.
   */
  private handleSelectedResult(value: SearchResultModal | SearchResultModal[]): void {
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item) {
          item.secondaryText = this.createSecondaryTextOnNames(item);
        }
      });
      this.selectedData = value.length > 0 ? value[0] : null;
      this.mostRecentData = null;
      this.multipleSelectedSearchedResult.emit(value);
    } else {
      if (value) {
        setTimeout(() => {
          value.contextPointId = value?.contextPointId ? value?.contextPointId : this.tabSelected?.activeData.contextPointId;
          value.secondaryText = this.tabSelected?.activeData?.secondaryText ? this.tabSelected?.activeData.secondaryText : this.createSecondaryTextOnNames(value);
        }, 50);
      }
      this.selectedData = value;
      this.mostRecentData = null;
      this.selectedSearchedResult.emit(value);
    }
  }

  /**
   * Formats a context point name with appropriate separator.
   */
  private formatContextPointName(element: { contextPointName: string }, index: number, totalLength: number): string {
    const isLastElement = index < totalLength - 1;
    const separator = isLastElement
      ? ' <img class="right-icon" src="../../assets/images/global-search/right-icon.svg" alt="icon" /> '
      : '';
    return element.contextPointName + separator;
  }

  /**
   * Toggles the rotation state of the arrow icon.
   */
  private toggleArrowRotation(): void {
    this.isRotated = !this.isRotated;
  }

  /**
   * Removes duplicate entries from the `selectionHierarchy` of a given item
   * by filtering out items that have the same `contextPointId` as the provided item's `contextPointId`.
   *
   * @param item - The object to process and remove duplicates from.
   * @returns The updated object with duplicates removed.
   */
  private removeDuplicateName<T extends SearchHistoryModal | SearchResultModal>(item: T): T {
    if (item.selectionHierarchy) {
      item.selectionHierarchy = item.selectionHierarchy.filter(
        hierarchyItem => hierarchyItem.contextPointId !== item.contextPointId
      );
    }
    return item;
  }

  /**
   * Activates the tab containing the most recent data and deactivates other tabs.
   * Sets the most recent data as activeData for the corresponding tab.
   */
  private activateTabWithMostRecentData(mostRecentData: SearchResultModal, tabIndex: number): void {
    if (!this.searchData?.SearchTabs?.length) {
      return;
    }

    this.searchData.SearchTabs.forEach((tab, index) => {
      if (index === tabIndex) {
        tab.activeData = {
          contextPointId: mostRecentData.contextPointId,
          contextPointName: mostRecentData.contextPointName,
          selectionHierarchy: mostRecentData.selectionHierarchy ?? [],
          secondaryText: mostRecentData.secondaryText
        };
        tab.isActive = true;
        this.tabSelected = tab;
      } else {
        delete tab.activeData;
        delete tab.isActive;
      }
    });
  }

  /**
   * Handles scenarios when no recent data is found in any tabs.
   * Checks for active data in tabs and activates the appropriate tab.
   * If no active data is found, keeps the first tab active by default.
   */
  private handleNoRecentDataScenario(): void {
    if (!this.searchData?.SearchTabs?.length) {
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }
    const tabWithActiveData = this.searchData.SearchTabs.find(tab => tab.activeData);
    if (tabWithActiveData) {
      this.searchData.SearchTabs.forEach((tab) => {
        if (tab === tabWithActiveData) {
          tab.isActive = true;
          this.saveRecentHistory(tab.activeData)
          this.tabSelected = tab;
        } else {
          tab.isActive = false;
        }
      });
    } else {
      this.searchData.SearchTabs.forEach((tab, index) => {
        tab.isActive = index === 0;
      });
      this.hasEmittedNull = true;
      this.selectedSearchedResult.emit(null);
      this.startActiveDataMonitoring();
    }
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  /*
   * Maps the provided search history results data with filter names based on the current tab value.
   * If the tab value name is 'Geo Region' or 'Sales Region', it maps the `contextPointSubTypeId` 
   * to the corresponding filter name from the respective naming array.
   * 
   * @param {SearchHistoryResultsModal[]} data - The search history results data to be mapped.
   * @returns {SearchHistoryResultsModal[]} - The mapped search history results data with filter names.
   */

  private mapWithFilterNaming(data: SearchHistoryResultsModal[], tab: tabValues): SearchHistoryResultsModal[] {
    if (this.translate.instant(tab.name) === this.translate.instant('COMMON.GLOBAL_SEARCH.GEOGRAPHY_REGION') || this.translate.instant(tab.name) === this.translate.instant('COMMON.GLOBAL_SEARCH.SALES_REGION')) {
      const namingArray = this.translate.instant(tab.name) === this.translate.instant('COMMON.GLOBAL_SEARCH.GEOGRAPHY_REGION') ? regionsContextPointSubTypeId : SalesRegionsContextPointSubTypeId
      const mappedArray = data.map((value) => {
        namingArray.forEach((element) => {
          if (value.contextPointSubTypeId === element.id) {
            value.filterName = element.name;
          }
        });
        return value;
      });
      return mappedArray;
    } else {
      return data
    }
  }

  private getContextSearchResult(searchBody: SearchRequesModel, contextPointIdKey: string): void {
    if (this.translate.instant(this.tabSelected.name) === this.translate.instant('COMMON.GLOBAL_SEARCH.SALES_REGION')) {
      searchBody.searchInFields = ['Accounts{SalesRegion,SalesRegionId,District,DistrictId,Territory,TerritoryId}']
      searchBody.filterBy = contextPointIdKey
        ? { [contextPointIdKey]: this.selectedData!.contextPointId.toString() }
        : { [this.tabSelected.contextPointIdKeys[0]]: this.selectedData!.contextPointId.toString() }

    }
    this.isLoading = true;
    this.cdr.markForCheck();
    this.subscriptions.push(
      this.globalSearchFiltersService.getSearchResult(searchBody, this.instanceId, this.timeOut).subscribe(
        {
          next: (results) => {
            if (results?.data) {
              if (results?.data?.searchResults?.length > 0) {
                this.selectedSearchedResult.emit({ ...results.data.searchResults[0], ...this.selectedData });
              }
            }
            this.isLoading = false;
            this.cdr.markForCheck();
          },
          error: () => {
            this.isLoading = false;
            this.cdr.markForCheck();
          },
        }
      )
    );
  }

  /**
   * Saves the recent search history for the current user context.
   * 
   * @param event - The active data containing context point information and selection hierarchy
   * @param event.contextPointId - The unique identifier for the context point
   * @param event.contextPointName - The display name of the context point
   * @param event.selectionHierarchy - Optional array representing the hierarchical selection path
   * 
   * @returns void
   * 
   * @remarks
   * This method constructs a history object with service type, application type, and context information,
   * then calls the global search filters service to persist the user's search history.
   * Sets loading state during the operation and handles both success and error scenarios.
   */
  private saveRecentHistory(event: ActiveData): void {
    this.isLoading = true;
    const saveHistory = {
      serviceType: this.searchData.ServiceType,
      contextPointId: event.contextPointId,
      contextPointName: event.contextPointName,
      applicationType: this.searchData.ApplicationType,
      selectionHierarchy: event.selectionHierarchy ? event.selectionHierarchy : [],
    };
    this.globalSearchFiltersService.saveUserContextSearchHistory(saveHistory).subscribe({
      next: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        if (error) {
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      },
    });
  }

  /**
   * Starts monitoring for active data changes after emitting null
   */
  private startActiveDataMonitoring(): void {
    if (this.activeDataCheckInterval) {
      clearInterval(this.activeDataCheckInterval);
    }

    this.activeDataCheckInterval = setInterval(() => {
      this.checkForActiveDataChanges();
    }, 100);
    setTimeout(() => {
      if (this.activeDataCheckInterval) {
        clearInterval(this.activeDataCheckInterval);
        this.activeDataCheckInterval = null;
      }
    }, 1000);
  }

  /**
   * Checks if any tab now has active data and saves it to recent history
   */
  private checkForActiveDataChanges(): void {
    if (!this.hasEmittedNull || !this.searchData?.SearchTabs?.length) {
      return;
    }
    const tabWithActiveData = this.searchData.SearchTabs.find(tab => tab.activeData);
    if (tabWithActiveData?.activeData) {
      this.hasEmittedNull = false;
      if (this.activeDataCheckInterval) {
        clearInterval(this.activeDataCheckInterval);
        this.activeDataCheckInterval = null;
      }
      this.searchData.SearchTabs.forEach((tab) => {
        if (tab === tabWithActiveData) {
          tab.isActive = true;
          this.tabSelected = tab;
          this.saveRecentHistory(tab.activeData);
        } else {
          tab.isActive = false;
        }
      });
    }
  }




}

